import { EManifestWasteUnit } from "encamp-prisma/generated/browser";

/**
 * Human-readable labels for the EPA e-Manifest waste unit codes.
 * G Gallons, K Kilograms, L Liters, M Metric Tons, N Cubic Meters,
 * P Pounds, T Tons (2000 lbs), Y Cubic Yards.
 * B Barrels (42 gallons) is an Encamp extension, not an EPA code — keep it
 * out of RCRAInfo-bound surfaces.
 */
export const EMANIFEST_WASTE_UNIT_LABELS: Record<EManifestWasteUnit, string> = {
  [EManifestWasteUnit.B]: "Barrels (42 gal)",
  [EManifestWasteUnit.G]: "Gallons",
  [EManifestWasteUnit.K]: "Kilograms",
  [EManifestWasteUnit.L]: "Liters",
  [EManifestWasteUnit.M]: "Metric Tons",
  [EManifestWasteUnit.N]: "Cubic Meters",
  [EManifestWasteUnit.P]: "Pounds",
  [EManifestWasteUnit.T]: "Tons",
  [EManifestWasteUnit.Y]: "Cubic Yards",
};

/**
 * The same units written the way they are said out loud, for places a value and
 * its unit sit together and the full word would be longer than the number.
 *
 * "lbs" rather than "P": the code is what RCRAInfo stores, not what a person
 * reading a quantity expects to see next to it.
 */
export const EMANIFEST_WASTE_UNIT_ABBREVIATIONS: Record<EManifestWasteUnit, string> = {
  [EManifestWasteUnit.B]: "bbl",
  [EManifestWasteUnit.G]: "gal",
  [EManifestWasteUnit.K]: "kg",
  [EManifestWasteUnit.L]: "L",
  [EManifestWasteUnit.M]: "t",
  [EManifestWasteUnit.N]: "m³",
  [EManifestWasteUnit.P]: "lbs",
  [EManifestWasteUnit.T]: "tons",
  [EManifestWasteUnit.Y]: "yd³",
};

/**
 * A quantity with its unit, e.g. `188 lbs`.
 *
 * qtyUnitOfMeasureCode is a free-form String column, so a value that is not an
 * EPA code is shown as it was recorded rather than dropped: the reviewer is
 * comparing two sources and needs to see what each one actually says.
 */
export function formatWasteQuantity(
  quantity: number | null | undefined,
  unitCode: string | null | undefined
): string {
  if (quantity == null) return "—";
  const amount = quantity.toLocaleString("en-US");
  const normalized = normalizeEManifestWasteUnit(unitCode);
  if (normalized) return `${amount} ${EMANIFEST_WASTE_UNIT_ABBREVIATIONS[normalized]}`;
  const raw = unitCode?.trim();
  return raw ? `${amount} ${raw}` : amount;
}

/**
 * Normalize a raw waste unit string to a valid EManifestWasteUnit code.
 *
 * Trims surrounding whitespace and upper-cases, so a miscased "p" or padded
 * " P " resolves to "P". Returns null when the value is empty or is not a
 * recognized EPA unit code — e.g. free text like "Box" that a reviewer may
 * type into the manifest review form by mistake.
 *
 * qtyUnitOfMeasureCode is a free-form String column, so callers must not
 * assume stored values are valid codes; run them through this first.
 */
export function normalizeEManifestWasteUnit(
  value: string | null | undefined
): EManifestWasteUnit | null {
  if (value == null) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === "") return null;
  return Object.values(EManifestWasteUnit).find((unit) => unit === normalized) ?? null;
}

/** Manifest corrections use EPA units; barrels are only an Encamp import extension. */
export function normalizeEpaWasteUnit(value: string | null | undefined): EManifestWasteUnit | null {
  const unit = normalizeEManifestWasteUnit(value);
  return unit === EManifestWasteUnit.B ? null : unit;
}
